import { Component, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  combineLatest,
  first,
  forkJoin,
  map,
  of,
  Observable,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { SplideSliderComponent } from 'src/app/core/components/splide-slider/splide-slider.component';
import { Constants } from 'src/app/core/constants';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { CompanyListItem } from 'src/app/core/models/company/company.interface';
import { AuthService } from 'src/app/core/services/auth.service';
import { CompanyService } from 'src/app/core/services/company.service';
import { FileService } from 'src/app/core/services/file.service';
import { InviteActionService } from 'src/app/core/services/invite-action.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { ThemeService } from 'src/app/core/services/theme.service';
import { CreateCompanyComponent } from './create-company/create-company.component';

interface CompaniesSlidesViewModel {
  companies: CompanyListItem[];
  invites: {
    id: string;
    companyId: string;
    senderFullName: string;
    companyName: string;
    imageUrl?: string;
    titleImageExtension?: string;
  }[];
}

@Component({
  selector: 'app-companies',
  templateUrl: './companies.component.html',
  styleUrls: ['./companies.component.scss'],
  providers: [InviteActionService],
  standalone: false,
})
export class CompaniesComponent {
  private readonly modalService = inject(NgbModal);
  private readonly companyService = inject(CompanyService);
  private readonly fileService = inject(FileService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly inviteActionService = inject(InviteActionService);
  private readonly permissionService = inject(PermissionService);
  private readonly themeService = inject(ThemeService);

  @ViewChild('spliderRef') spliderRef!: SplideSliderComponent;

  public readonly notFound = Constants.NotFound;

  public isLoading = false;

  public readonly companies$ = this.companyService.updateCompanies$.pipe(
    startWith(null),
    tap(() => this.setLoading(true)),
    switchMap(() => this.companyService.getCompanies()),
    switchMap((companies) =>
      this.resolveImageUrls(
        companies,
        (company) => `companies/${company.id}/title${company.titleImageExtension}`,
      ),
    ),
    tap(() => this.setLoading(false)),
    shareReplay(1),
  );

  public readonly companyInvites$ = this.authService.user$.pipe(
    switchMap((user) =>
      this.resolveImageUrls(
        user.data?.invites ?? [],
        (invite) =>
          `companies/${invite.companyId}/title${invite.titleImageExtension}`,
      ),
    ),
    shareReplay(1),
  );

  public readonly allSlides$ = combineLatest([
    this.companies$,
    this.companyInvites$,
  ]).pipe(
    map(
      ([companies, invites]): CompaniesSlidesViewModel => ({
        companies,
        invites,
      }),
    ),
    shareReplay(1),
  );

  public readonly showCompanyHint$ = combineLatest([
    this.companies$,
    this.companyInvites$,
  ]).pipe(
    map(([companies, invites]) => companies.length === 0 && invites.length === 0),
  );

  public readonly theme$ = this.themeService.themeChanges;

  public createCompany(): void {
    this.modalService.open(CreateCompanyComponent, {
      size: 'lg',
      windowClass: 'br-50',
    });
  }

  public openCompany(company: CompanyListItem): void {
    this.companyService
      .updateCompanyLastActivityTime(company.id)
      .pipe(
        first(),
        switchMap(() => {
          this.permissionService.setPermissionsUrl(company.id);

          return this.permissionService
            .getUserCompanyPermissions(this.authService.user$.getValue().data!.id)
            .pipe(
              map((permissions) =>
                permissions
                  .filter((permission) => permission.permissionCode === UserCompanyPermissions.View)
                  .map((permission) => permission.resourceType),
              ),
            );
        }),
        tap((viewResourceTypes) => {
          if (viewResourceTypes.includes(ResourceTypes.Project)) {
            void this.router.navigateByUrl(`companies/${company.id}/projects`);
            return;
          }

          if (viewResourceTypes.includes(ResourceTypes.Employee)) {
            void this.router.navigateByUrl(`companies/${company.id}/employees`);
            return;
          }

          if (viewResourceTypes.includes(ResourceTypes.Organization)) {
            void this.router.navigateByUrl(`companies/${company.id}/crm/organizations`);
            return;
          }

          void this.router.navigateByUrl(`companies/${company.id}/crm/contacts`);
        }),
      )
      .subscribe();
  }

  public acceptInvite(id: string): void {
    this.isLoading = true;

    this.inviteActionService
      .acceptInvite(id)
      .pipe(
        first(),
        tap(() => this.setLoading(false)),
      )
      .subscribe();
  }

  public rejectInvite(id: string): void {
    this.isLoading = true;

    this.inviteActionService
      .rejectInvite(id)
      .pipe(
        first(),
        tap(() => this.setLoading(false)),
      )
      .subscribe();
  }

  private setLoading(value: boolean): void {
    if (this.isLoading === value) {
      return;
    }
    // Defer to a fresh microtask so the flag never flips inside a CD pass
    // that already read its previous value (NG0100 guard for the
    // companies$ pipe whose `tap` runs synchronously on subscription).
    queueMicrotask(() => {
      this.isLoading = value;
    });
  }

  private resolveImageUrls<
    T extends { titleImageExtension?: string; imageUrl?: string },
  >(items: T[], getPath: (item: T) => string): Observable<T[]> {
    if (!items.length) {
      return of([]);
    }

    return forkJoin(
      items.map((item) => {
        if (!item.titleImageExtension) {
          item.imageUrl = Constants.NotFound;
          return of(item);
        }

        return this.fileService.getEntityWithImageUrl(item, getPath(item));
      }),
    );
  }
}
